/**
 * CardBadges.js
 *
 * Badge components for overlay on card images.
 * Includes event badges, date badges, count badges, etc.
 */

import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { CONTENT_TYPES } from '../../utils/normalizeCardData';
import { parseLocalDate, getDisplayLocale } from '../../utils/dateUtils';
import { formatList } from '../../utils/stringUtils';
import Icon from '../shared/Icon';

/**
 * Event badge - shows for Ticketmaster events
 */
export const EventBadge = ({ className = '' }) => {
  const { t } = useTranslation('feedCard');
  return (
    <div className={`px-2 py-1 bg-primary-lighter text-primary-dark text-xs font-medium rounded-full flex items-center space-x-1 ${className}`}>
      <Icon name="ticket" className="w-3.5 h-3.5" />
      <span>{t('badges.event.label')}</span>
    </div>
  );
};

/**
 * New badge - "newly announced" pill for events surfaced by a follow
 * (What's On strip pins, /whats-on band 1). Driven by typeData.isNew,
 * which the surface sets on the raw item (_isNew) before normalization.
 */
export const NewBadge = ({ className = '' }) => {
  const { t } = useTranslation('feedCard');
  return (
    <div className={`px-2 py-0.5 bg-primary text-content-inverse text-xs font-semibold rounded-full shadow-card w-fit ${className}`}>
      {t('badges.newLabel')}
    </div>
  );
};

/**
 * Date badge - shows date range for trips, events
 */
export const DateBadge = ({ dateDisplay, className = '' }) => {
  if (!dateDisplay) return null;

  return (
    <div className={`bg-primary bg-opacity-90 text-content-inverse text-xs font-bold px-2 py-1 rounded-lg ${className}`}>
      {dateDisplay}
    </div>
  );
};

/**
 * Duration badge - shows trip duration, tour time
 */
export const DurationBadge = ({ duration, icon = 'airplane', className = '' }) => {
  if (!duration) return null;

  return (
    <div className={`bg-black/60 text-content-inverse text-xs px-2 py-1 rounded-lg flex items-center gap-1 ${className}`}>
      <Icon name={icon} className="w-3.5 h-3.5" />
      <span>{duration}</span>
    </div>
  );
};

/**
 * Count badge - shows item count, photo count, etc.
 */
export const CountBadge = ({ count, label, className = '' }) => {
  if (!count) return null;

  return (
    <div className={`bg-black/50 text-content-inverse text-xs px-2 py-1 rounded-full ${className}`}>
      {count} {label}
    </div>
  );
};

/**
 * Type indicator badge - shows content type in carousel slides
 */
export const TypeIndicatorBadge = ({ type, className = '' }) => {
  if (!type || type === 'activity') return null;

  return (
    <div className={`bg-black/50 text-content-inverse text-xs px-2 py-1 rounded-full capitalize ${className}`}>
      {type.replace('_', ' ')}
    </div>
  );
};

// Visibility badge config - key stems + non-translatable data (icon, color
// classes) — labels live in src/locales/en/feedCard.json under
// badges.visibility.<key>.label.
const VISIBILITY_CONFIG = {
  private: { key: 'private', icon: 'lock', bgClass: 'bg-background', textClass: 'text-content-secondary' },
  friends: { key: 'friends', icon: 'userGroup', bgClass: 'bg-info-light', textClass: 'text-info-dark' }
};

/**
 * Visibility badge - shows public/private/friends status
 */
export const VisibilityBadge = ({ visibility, className = '' }) => {
  const { t } = useTranslation('feedCard');
  if (!visibility || visibility === 'public') return null;

  const { key, icon, bgClass, textClass } = VISIBILITY_CONFIG[visibility] || VISIBILITY_CONFIG.private;

  return (
    <div className={`px-2 py-1 ${bgClass} ${textClass} text-xs font-medium rounded-full flex items-center space-x-1 ${className}`}>
      <Icon name={icon} className="w-3.5 h-3.5" />
      <span>{t(`badges.visibility.${key}.label`)}</span>
    </div>
  );
};

/**
 * Archived badge - marks a collection/board as put away.
 *
 * Only rendered in the archive view, where every card is archived; it exists so
 * a card lifted out of that context (a screenshot, a future mixed list) still
 * reads as archived rather than live.
 */
export const ArchivedBadge = ({ className = '' }) => {
  const { t } = useTranslation('feedCard');
  return (
    <div className={`px-2 py-1 bg-surface-secondary text-content-secondary text-xs font-medium rounded-full flex items-center space-x-1 ${className}`}>
      <Icon name="archive" className="w-3.5 h-3.5" />
      <span>{t('badges.archived.label')}</span>
    </div>
  );
};

/**
 * Sponsored badge - EU DSA Article 26 compliant label for boosted content
 * Shows advertiser identity and provides "why this ad?" disclosure trigger
 */
export const SponsoredBadge = ({ sponsorName, onInfoClick, className = '' }) => {
  const { t } = useTranslation('feedCard');
  return (
    <div className={`px-2 py-1 bg-black/50 text-content-inverse text-xs font-medium rounded-full flex items-center space-x-1 ${className}`}>
      <span>{sponsorName ? t('badges.sponsoredBadge.labelWithName', { sponsorName }) : t('badges.sponsoredBadge.label')}</span>
      <button
        onClick={(e) => { e.stopPropagation(); onInfoClick?.(); }}
        className="ml-0.5 w-4 h-4 rounded-full bg-content-inverse/20 hover:bg-content-inverse/40 flex items-center justify-center transition-colors flex-shrink-0"
        aria-label={t('badges.sponsoredBadge.infoAriaLabel')}
      >
        <span className="text-[10px] font-bold leading-none">{t('badges.sponsoredBadge.infoIcon')}</span>
      </button>
    </div>
  );
};

/**
 * Inline Sponsored label for compact/list cards (search results) where the
 * overlay badge + disclosure modal don't fit. Same DSA Article 26 labeling;
 * the disclosure is carried in the title tooltip.
 */
export const SponsoredInlineLabel = ({ sponsorName, className = '' }) => {
  const { t } = useTranslation('feedCard');
  return (
    <span
      className={`px-1.5 py-0.5 rounded-lg text-xs font-medium bg-surface-secondary text-content-secondary border border-border whitespace-nowrap ${className}`}
      title={sponsorName ? t('badges.sponsoredInlineLabel.titleWithName', { sponsorName }) : t('badges.sponsoredInlineLabel.title')}
    >
      {sponsorName ? t('badges.sponsoredInlineLabel.labelWithName', { sponsorName }) : t('badges.sponsoredInlineLabel.label')}
    </span>
  );
};

/**
 * Friend social proof badge - shows "Liked by [friend] and X others"
 * Rendered below the creator row in the content section (not as an overlay).
 */
export const FriendSocialProofBadge = ({ socialProof, className = '' }) => {
  if (!socialProof) return null;

  return (
    <div className={`flex items-center gap-1.5 text-xs text-content-secondary ${className}`}>
      <span className="truncate">{socialProof.display_text}</span>
    </div>
  );
};

/**
 * Combined overlay badges component
 * Renders appropriate badges based on content type and data
 */
const CardBadges = ({
  type,
  typeData,
  position = 'top-left', // top-left, top-right, bottom-left, bottom-right
  className = ''
}) => {
  const { t } = useTranslation('feedCard');
  const positionClasses = {
    'top-left': 'top-2 left-2',
    'top-right': 'top-2 right-2',
    'bottom-left': 'bottom-2 left-2',
    'bottom-right': 'bottom-2 right-2'
  };

  const renderBadges = () => {
    switch (type) {
      case CONTENT_TYPES.ACTIVITY:
        if (typeData?.isEvent) {
          return <EventBadge />;
        }
        return null;

      case CONTENT_TYPES.TRIP:
        // Dates and duration are already shown in TripContent below the photo
        return null;

      case CONTENT_TYPES.WALKING_TOUR:
        return (
          <div className="flex flex-col gap-1">
            {typeData?.duration && (
              <DurationBadge duration={typeData.duration} icon="walking" />
            )}
            {typeData?.stopCount > 0 && (
              <CountBadge count={typeData.stopCount} label={t('badges.walkingTour.stopsLabel', { count: typeData.stopCount })} />
            )}
          </div>
        );

      case CONTENT_TYPES.COLLECTION:
      case CONTENT_TYPES.BOARD:
        // Visibility shown inline with title in content section
        return null;

      case CONTENT_TYPES.ALBUM:
        // Photo/place counts are surfaced in the content section below the
        // photo (see AlbumContent stats line). Keep the image overlay clean.
        return null;

      case CONTENT_TYPES.EVENT_GROUP:
        return (
          <div className="flex flex-col gap-1">
            {typeData?.isNew && <NewBadge />}
            {typeData?.frequency ? (
              <DateBadge dateDisplay={formatFrequency(typeData.frequency, t)} />
            ) : typeData?.startDate && (
              <DateBadge dateDisplay={formatDateRange(typeData.startDate, typeData.endDate)} />
            )}
            {typeData?.matchType === 'direct' && (
              <div className="bg-accent/90 text-primary-dark text-sm font-bold w-7 h-7 rounded-full flex items-center justify-center" title={t('badges.eventGroup.favoriteArtistTitle')}>
                <Icon name="star" className="w-4 h-4" />
              </div>
            )}
          </div>
        );

      case CONTENT_TYPES.ARTIST_EVENT:
        return (
          <div className="flex flex-col gap-1">
            {typeData?.isNew && <NewBadge />}
            {typeData?.eventDate && (
              <DateBadge dateDisplay={formatEventDate(typeData.eventDate)} />
            )}
            {typeData?.matchType === 'direct' && (
              <div className="bg-accent/90 text-primary-dark text-sm font-bold w-7 h-7 rounded-full flex items-center justify-center" title={t('badges.artistEvent.pickedForYouTitle')}>
                <Icon name="star" className="w-4 h-4" />
              </div>
            )}
          </div>
        );

      case CONTENT_TYPES.EVENT:
        return (
          <div className="flex flex-col gap-1">
            {typeData?.isNew && <NewBadge />}
            {/* An open-ended venue's event_date is the last sync date, so it
                says how OFTEN it is open instead of naming a stale day. */}
            {typeData?.frequency ? (
              <DateBadge dateDisplay={formatFrequency(typeData.frequency, t)} />
            ) : typeData?.eventDate && (
              <DateBadge dateDisplay={formatDateRange(typeData.eventDate, typeData.endDate)} />
            )}
            {typeData?.matchType === 'direct' && (
              <div className="bg-accent/90 text-primary-dark text-sm font-bold w-7 h-7 rounded-full flex items-center justify-center" title={t('badges.event.favoriteArtistTitle')}>
                <Icon name="star" className="w-4 h-4" />
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  const badges = renderBadges();
  if (!badges) return null;

  return (
    <div className={`absolute ${positionClasses[position]} z-raised ${className}`}>
      {badges}
    </div>
  );
};

// Event surfaces list up to 120 days ahead, so a badge can name a date in
// NEXT year — "Jan 3" alone is ambiguous from October onward. Show the year
// exactly when it isn't the current one (ShowCard's yearWhenNotThisYear rule).
const dateOptionsFor = (date) => (
  date.getFullYear() === new Date().getFullYear()
    ? { month: 'short', day: 'numeric' }
    : { month: 'short', day: 'numeric', year: 'numeric' }
);

// Open-ended venues get a frequency instead of a date. Day NAMES come from
// Intl rather than seven keys per language; only the joining copy is
// translated, because the plural in "Saturdays" and the separator in
// "Tue, Thu, Sat" are the parts that actually differ between locales.
//
// 2026-09-06 is a Sunday, so index 0..6 maps onto Sun..Sat by adding days.
// Formatted in UTC so a negative-offset timezone cannot roll the reference
// date back and shift every name by one.
const WEEK_REF_SUNDAY_UTC = Date.UTC(2026, 8, 6);
const dayName = (index, weekday) =>
  new Date(WEEK_REF_SUNDAY_UTC + index * 86400000)
    .toLocaleDateString(getDisplayLocale(), { weekday, timeZone: 'UTC' });

/**
 * Render a venueFrequency shape as badge copy.
 * @param {object} frequency - from src/utils/venueFrequency.js
 * @param {function} t - the feedCard-namespaced translator
 */
const formatFrequency = (frequency, t) => {
  switch (frequency?.kind) {
    case 'daily':
      return t('badges.frequency.daily');
    case 'weekly':
      // Long name: "Saturdays" reads as a standing weekly thing in a way
      // "Sat" does not, and this is the farmers-market case.
      return t('badges.frequency.weekly', { day: dayName(frequency.day, 'long') });
    case 'closed':
      // "Closed Mondays" beats the wrapping run "Tue-Sun" for the same set.
      return t('badges.frequency.closed', { day: dayName(frequency.day, 'long') });
    case 'range':
      return t('badges.frequency.range', {
        from: dayName(frequency.from, 'short'),
        to: dayName(frequency.to, 'short')
      });
    case 'list':
      // 'unit' rather than the default 'conjunction': a badge wants
      // "Tue, Thu, Sat", not "Tue, Thu, and Sat". English is byte-identical to
      // a plain join, but Dutch is not — Intl gives "di, do en za" — which is
      // exactly why docs/I18N.md forbids hand-joining a list.
      return t('badges.frequency.list', {
        days: formatList(frequency.days.map((d) => dayName(d, 'short')), 'unit')
      });
    case 'ongoing':
      return t('badges.frequency.ongoing');
    default:
      return null;
  }
};

/**
 * Helper to format date range
 */
const formatDateRange = (startDate, endDate) => {
  if (!startDate) return null;

  const start = parseLocalDate(startDate);
  const end = endDate ? parseLocalDate(endDate) : null;

  // Same-day events (end_date == event_date with only end_time differing)
  // and junk ranges (end before start) collapse to a single date.
  if (!end || end.getTime() <= start.getTime()) {
    return start.toLocaleDateString(getDisplayLocale(), dateOptionsFor(start));
  }

  const startMonth = start.toLocaleDateString(getDisplayLocale(), { month: 'short' });
  const endMonth = end.toLocaleDateString(getDisplayLocale(), { month: 'short' });

  if (startMonth === endMonth && start.getFullYear() === end.getFullYear()
      && start.getFullYear() === new Date().getFullYear()) {
    return `${startMonth} ${start.getDate()}-${end.getDate()}`;
  }

  return `${start.toLocaleDateString(getDisplayLocale(), dateOptionsFor(start))} - ${end.toLocaleDateString(getDisplayLocale(), dateOptionsFor(end))}`;
};

/**
 * Helper to format a single event date
 */
const formatEventDate = (dateStr) => {
  if (!dateStr) return null;
  const date = parseLocalDate(dateStr);
  return date.toLocaleDateString(getDisplayLocale(), dateOptionsFor(date));
};

export default memo(CardBadges);
